import { hasPermission } from '../../../permissions/usePermissions';
import PermissionButton from '../../../permissions/PermissionButton';
import React, { useState, useEffect, useRef } from 'react';
import { useFetchAllBonlivPerEtageOverview, useFetchAllBonliv, useRecreateBonlivPerEtageForAll } from '../../../hooks/bonlivHooks';
import { useFetchAllEntrepots } from '../../../hooks/entrepotHooks';
import CollectionOverviewList from '../collection/CollectionOverviewList';
import { markBonlivPerEtagePrinted } from '../../../models/bonlivAPI';
import { useSelector } from 'react-redux';
import {
  Paper,
  CircularProgress,
  Typography,
  Box,
  Container,
  Button,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import { ExportOutlined, ReloadOutlined, PrinterOutlined, ToolOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const PreparationOverviewList = ({ onViewDetails }) => {
  const user = useSelector(state => state.user);
  const initialEtage = localStorage.getItem('activeEtage') || 'all';
  const initialEntrepot = localStorage.getItem('activeEntrepot') || (user?.CODE_ENT || 'all');
  const initialTab = parseInt(localStorage.getItem('activePreparationTab') || '0', 10);
  const [activeEtage, setActiveEtage] = useState(initialEtage);
  const [activeEntrepot, setActiveEntrepot] = useState(initialEntrepot);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeFilter, setActiveFilter] = useState('ajd');
  const [clientSearch, setClientSearch] = useState('');
  const [refDevSearch, setRefDevSearch] = useState('');
  const collectionRefreshRef = useRef(null);
  const [selectedRefBLs, setSelectedRefBLs] = useState(new Set());
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printSelection, setPrintSelection] = useState(new Set());
  const [printModalStatusFilter, setPrintModalStatusFilter] = useState('all'); // 'all', 'SIGNALE', 'ATTENTE'
  const [printModalImprimerTab, setPrintModalImprimerTab] = useState('nonImprime'); // 'nonImprime' | 'imprime'
  
  // State for BonlivPerEtage fix dialog and notifications
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Fetch available entrepots
  const { entrepots, loading: entrepotsLoading } = useFetchAllEntrepots();
  
  // Hook for recreating BonlivPerEtage
  const { recreateAll, loading: recreateLoading } = useRecreateBonlivPerEtageForAll();

  // Function to check if a bonliv is a complementary order
  const isComplementaryOrder = (remarque) => {
    if (!remarque) return false;
    // Check for patterns like @COMP123, @COM123, @CMP123, etc.
    return /@(COMP|COM|CMP)\s*\d{3,4}/i.test(remarque);
  };

  // Function to check if a devis is unique (doesn't have other étages)
  const isUniqueDevis = (row) => {
    if (activeTab !== 0) return false; // Only applicable for preparation tab
    if (!row.allEtages || row.allEtages.length === 0) return true;
    
    // Handle both old format (number) and new format (object with etage and status)
    const otherEtages = row.allEtages.filter(etageInfo => {
      const etageNumber = typeof etageInfo === 'object' ? etageInfo.etage : etageInfo;
      return etageNumber !== row.Etage;
    });
    
    return otherEtages.length === 0;
  };

  // Check user permissions
  const isPreparateur = user?.PreparationPermission;
  const isControleur = user?.ControlePermission;
  const isManager = user?.IsManager;
  const canManageDeliveryNote = user?.CanManageDeliveryNote;
  
  // Determine which tabs to show based on user permissions
  const getVisibleTabs = () => {
    const tabs = [];
    if (hasPermission(user, 'preparation')) tabs.push({ id: 0, label: 'Préparation', key: 'preparation' });
    if (hasPermission(user, 'control')) tabs.push({ id: 1, label: 'Contrôle', key: 'control' });
    if (hasPermission(user, 'control') || hasPermission(user, 'deliveryNotes')) tabs.push({ id: 2, label: 'Validés', key: 'validated' });
    if (hasPermission(user, 'collection')) tabs.push({ id: 3, label: 'Collecte', key: 'collection' });
    return tabs;
  };

  const visibleTabs = getVisibleTabs();

  // Ensure activeTab is valid for current user permissions - MOVED TO TOP
  useEffect(() => {
    const validTabIds = visibleTabs.map(tab => tab.id);
    if (!validTabIds.includes(activeTab) && visibleTabs.length > 0) {
      // Switch to the first available tab
      const defaultTab = visibleTabs.length > 1 ? visibleTabs[1].id : visibleTabs[0].id;
      setActiveTab(defaultTab);
      localStorage.setItem('activePreparationTab', defaultTab.toString());
    }
  }, [visibleTabs, activeTab]);

  const { 
    overviewData, 
    total, 
    loading, 
    error, 
    refetch, 
    updateParams,
    availableEtages = []
  } = useFetchAllBonlivPerEtageOverview({
    etage: activeEtage,
    entrepot: activeEntrepot,
    page: page + 1,
    pageSize: rowsPerPage,
    filter: activeFilter,
    clientSearch: clientSearch || undefined,
    refDevSearch: refDevSearch || undefined
  });

  
  const { 
    bonliv: preparedBonlivs, 
    total: preparedTotal, 
    loading: preparedLoading, 
    error: preparedError, 
    refetch: refetchPrepared 
  } = useFetchAllBonliv({
    page: page + 1,
    pageSize: rowsPerPage,
    status: 'PREPARE',
    filter: activeFilter,
    entrepot: activeEntrepot,
    clientSearch: clientSearch || undefined,
    refDevSearch: refDevSearch || undefined
  });

  const { 
    bonliv: valideBonlivs, 
    total: valideTotal, 
    loading: valideLoading, 
    error: valideError, 
    refetch: refetchValide 
  } = useFetchAllBonliv({
    page: page + 1,
    pageSize: rowsPerPage,
    status: 'VALIDE',
    filter: activeFilter,
    entrepot: activeEntrepot,
    clientSearch: clientSearch || undefined,
    refDevSearch: refDevSearch || undefined
  });

  // Early return for access denied - NOW comes after all hooks
  if (visibleTabs.length === 0) {
    return (
      <Container maxWidth="xl" style={{ marginTop: '2rem' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" flexDirection="column">
          <Typography variant="h6" color="error" gutterBottom>
            Accès refusé
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Vous n'avez pas les permissions nécessaires pour accéder à cette section.
          </Typography>
        </Box>
      </Container>
    );
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
    if (activeTab === 0) {
      updateParams({ 
        page: newPage, 
        pageSize: rowsPerPage,
        etage: activeEtage,
        entrepot: activeEntrepot,
        filter: activeFilter,
        clientSearch: clientSearch || undefined,
        refDevSearch: refDevSearch || undefined
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    if (activeTab === 0) {
      updateParams({ 
        page: 1, 
        pageSize: newSize,
        etage: activeEtage,
        entrepot: activeEntrepot,
        filter: activeFilter,
        clientSearch: clientSearch || undefined,
        refDevSearch: refDevSearch || undefined
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    
    // If switching away from preparation tab and currently on signale filter, reset to 'all'
    if (newValue !== 0 && activeFilter === 'signale') {
      setActiveFilter('all');
    }
    
    localStorage.setItem('activePreparationTab', newValue.toString());
  };

  const handleEntrepotChange = (newEntrepot) => {
    setActiveEntrepot(newEntrepot);
    setPage(0);
    localStorage.setItem('activeEntrepot', newEntrepot);
    
    // Update params for preparation tab (tab 0)
    if (activeTab === 0) {
      updateParams({ 
        page: 1, 
        pageSize: rowsPerPage, 
        etage: activeEtage,
        entrepot: newEntrepot,
        filter: activeFilter,
        clientSearch: clientSearch || undefined,
        refDevSearch: refDevSearch || undefined
      });
    }
    // For tabs 1 and 2, the hook will automatically refetch when entrepot changes
    // because it's included in the params dependency
  };

  const handleEtageChange = (activeKey) => {
    setActiveEtage(activeKey);
    setPage(0);
    updateParams({ 
      page: 1, 
      pageSize: rowsPerPage, 
      etage: activeKey,
      entrepot: activeEntrepot,
      filter: activeFilter,
      clientSearch: clientSearch || undefined,
      refDevSearch: refDevSearch || undefined
    });
    localStorage.setItem('activeEtage', activeKey);
  };

  const handleFilterChange = (event, newValue) => {
    setActiveFilter(newValue);
    setPage(0);
    // Update params for preparation tab to trigger refetch
    if (activeTab === 0) {
      updateParams({ 
        page: 1, 
        pageSize: rowsPerPage, 
        etage: activeEtage,
        entrepot: activeEntrepot,
        filter: newValue,
        clientSearch: clientSearch || undefined,
        refDevSearch: refDevSearch || undefined
      });
    }
  };

  const handleClientSearchChange = (event) => {
    setClientSearch(event.target.value);
  };

  const handleRefDevSearchChange = (event) => {
    setRefDevSearch(event.target.value);
  };

  const handleSearch = () => {
    setPage(0);
    // Update params for preparation tab to trigger refetch
    if (activeTab === 0) {
      updateParams({ 
        page: 1, 
        pageSize: rowsPerPage, 
        etage: activeEtage,
        entrepot: activeEntrepot,
        filter: activeFilter,
        clientSearch: clientSearch || undefined,
        refDevSearch: refDevSearch || undefined
      });
    }
  };

  const handleClearSearch = () => {
    setClientSearch('');
    setRefDevSearch('');
    setPage(0);
    // Update params for preparation tab to trigger refetch
    if (activeTab === 0) {
      updateParams({ 
        page: 1, 
        pageSize: rowsPerPage, 
        etage: activeEtage,
        filter: activeFilter,
        clientSearch: undefined,
        refDevSearch: undefined
      });
    }
  };
  
  // Handler to open fix confirmation dialog
  const handleOpenFixDialog = () => {
    setFixDialogOpen(true);
  };
  
  // Handler to close fix dialog
  const handleCloseFixDialog = () => {
    setFixDialogOpen(false);
  };
  
  // Handler to execute the BonlivPerEtage fix
  const handleExecuteFix = async () => {
    try {
      const result = await recreateAll();
      setFixDialogOpen(false);
      
      // Show success message
      setSnackbarMessage(
        `✅ Succès! ${result.data?.processed || 0} BonLiv traités, ${result.data?.failed || 0} échoués`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Refresh the data
      if (activeTab === 0) refetch();
      else if (activeTab === 1) refetchPrepared();
      else if (activeTab === 2) refetchValide();
      
      // Log details for debugging
      console.log('BonlivPerEtage fix result:', result);
    } catch (error) {
      setFixDialogOpen(false);
      setSnackbarMessage(`❌ Erreur: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error fixing BonlivPerEtage:', error);
    }
  };
  
  // Handler to close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const tabItems = [
    { key: 'all', label: 'TOUT' },
    ...(availableEtages.length > 0 ? availableEtages : [-1, 0, 1, 2]).map(etage => ({
      key: etage.toString(),
      label: `Étage ${etage}`
    }))
  ];

  if ((loading && !overviewData.length) || 
      (preparedLoading && !preparedBonlivs?.length) || 
      (valideLoading && !valideBonlivs?.length)) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: 16 }}>Chargement...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || preparedError || valideError) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" flexDirection="column">
          <Typography color="error" variant="h6">Erreur lors du chargement des données:</Typography>
          <Typography color="error">{error || preparedError || valideError}</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={
              activeTab === 0 ? refetch : 
              activeTab === 1 ? refetchPrepared : 
              refetchValide
            } 
            style={{ marginTop: '1rem' }}
          >
            Réessayer
          </Button>
        </Box>
      </Container>
    );
  }

  const renderSearchSection = () => (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        {(activeTab === 1 || activeTab === 2) && (
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="entrepot-select-label-search">Entrepôt</InputLabel>
              <Select
                labelId="entrepot-select-label-search"
                value={activeEntrepot}
                onChange={(e) => handleEntrepotChange(e.target.value)}
                label="Entrepôt"
              >
                <MenuItem value="all">Tous les entrepôts</MenuItem>
                {entrepots?.map((entrepot) => (
                  <MenuItem key={entrepot.code_ent} value={entrepot.code_ent}>
                    {entrepot.code_ent} - {entrepot.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={activeTab === 1 || activeTab === 2 ? 2.5 : 3}>
          <TextField
            fullWidth
            size="small"
            label="Rechercher par client"
            variant="outlined"
            value={clientSearch}
            onChange={handleClientSearchChange}
            placeholder="Nom du client..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={activeTab === 1 || activeTab === 2 ? 2.5 : 3}>
          <TextField
            fullWidth
            size="small"
            label="Rechercher par REF_DEV"
            variant="outlined"
            value={refDevSearch}
            onChange={handleRefDevSearchChange}
            placeholder="Référence devis..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={activeTab === 1 || activeTab === 2 ? 2.5 : 3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={!clientSearch && !refDevSearch}
            fullWidth
          >
            Rechercher
          </Button>
        </Grid>
        <Grid item xs={12} sm={activeTab === 1 || activeTab === 2 ? 2.5 : 3}>
          <Button
            variant="outlined"
            onClick={handleClearSearch}
            disabled={!clientSearch && !refDevSearch}
            fullWidth
          >
            Effacer recherche
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

const renderFilterTabs = () => (
  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
    <Tabs 
      value={activeFilter} 
      onChange={handleFilterChange}
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="Tous" value="all" />
      <Tab label="AJD" value="ajd" />
      {activeTab === 0 && <Tab label="Unique" value="unique" />}   {/* 👈 only on Préparation */}
      <Tab label="Sur Place" value="surplace" />
      <Tab label="Nos Soins" value="nossoins" />
      <Tab label="Normal" value="normal" />
      {activeTab === 0 && <Tab label="Signalé" value="signale" />}
    </Tabs>
  </Box>
);


  const renderCardView = () => {
    let data = 
      activeTab === 0 ? overviewData : 
      activeTab === 1 ? preparedBonlivs : 
      valideBonlivs;
    
    // Apply unique filter if selected (frontend logic for unique devis detection)
    // Note: The basic filtering (no AJD, no SUR_PLACE, no SIGNALE) is now handled by backend
    // But we still need to filter for devis that don't have other etages (unique logic)
    if (activeFilter === 'unique' && activeTab === 0) {
      data = data.filter(row => isUniqueDevis(row));
    }
    
    // Signale filtering is now fully handled by the backend for preparation tab.
    // For non-preparation tabs, we still need to exclude SIGNALE rows since they use different endpoints
    if (activeTab !== 0) {
      // Other tabs: exclude rows with status 'SIGNALE'
      data = data.filter(row => row.status !== 'SIGNALE');
    }
    
    const totalCount = 
      activeTab === 0 ? (activeFilter === 'unique' ? data.length : total) : 
      activeTab === 1 ? preparedTotal : 
      valideTotal;

    const pageCount = Math.ceil(totalCount / rowsPerPage);

    return (
      <>
        {activeTab !== 3 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <PermissionButton permissionModule="preparation" permissionAction="print" permissionLibrary="mui"
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setPrintSelection(new Set());
                setPrintModalOpen(true);
              }}
            >
              Imprimer
            </PermissionButton>
          </Box>
        )}
        {activeTab !== 3 && renderSearchSection()}
        {activeTab !== 3 && renderFilterTabs()}
        <Box>
          {data.length > 0 ? (
            data.map((row) => (
              <Card 
                key={row.id}
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  boxShadow: 2,
                  mb: 1.5,
                  borderLeft: activeTab === 0 && row.status === 'SIGNALE' ? '4px solid #4caf50' :
                             activeTab === 0 && row.status === 'VALIDE' ? '4px solid #4caf50' : 
                             activeTab === 0 ? '4px solid #f44336' : 
                             row.status === 'VALIDE' ? '4px solid #4caf50' : '4px solid #f44336'
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
                        {row.REF_BL}
                      </Typography>
                      {activeTab === 0 && (
                        <Chip 
                          label={`Étage ${row.Etage} (${row.itemCount})`} 
                          color="primary" 
                          size="small"
                        />
                      )}
                      {activeTab === 0 && row.imprimer === true && (
                        <Chip 
                          icon={<PrinterOutlined />}
                          label="Imprimé" 
                          size="small"
                          sx={{ 
                            ml: 1,
                            backgroundColor: '#00897b',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {activeTab === 0 && row.status && (
                        <Chip 
                          label={row.status} 
                          color={row.status === 'SIGNALE' || row.status === 'VALIDE' ? 'success' : 'warning'}
                          size="small"
                        />
                      )}
                      {/* Unique devis badge */}
                      {isUniqueDevis(row) && (
                        <Chip 
                          label="UNIQUE" 
                          color="info"
                          variant="outlined"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {/* Complementary order badge */}
                      {isComplementaryOrder(activeTab === 0 ? row.bonliv?.REMARQUE : row.REMARQUE) && (
                        <Chip 
                          label="COMP" 
                          color="secondary"
                          variant="outlined"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      <Tooltip title="Voir les détails pour cette préparation de Bonliv">
                        <IconButton
                          color="primary"
                          onClick={() => onViewDetails(activeTab === 0 ? row.bonliv?.id : row.id)}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          <ExportOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimer (emplacement)">
                        <IconButton
                          color="primary"
                          onClick={async () => {
                            const { printEmplacementForBonliv } = await import('./PreparationEmplacementPrintUtility');
                            const header = activeTab === 0 ? {
                              REF_BL: row.REF_BL,
                              REF_DEV: row.bonliv?.REF_DEV,
                              DATE_BL: row.bonliv?.DATE_BL,
                              COMPTE: row.bonliv?.COMPTE,
                              CLIENT: row.bonliv?.CLIENT,
                              VILLE: row.bonliv?.client?.VILLE || row.bonliv?.VILLE,
                              ADRESSE: row.bonliv?.client?.ADRESSE_SIEGE || row.bonliv?.ADRESSE,
                              MODELIV: row.bonliv?.MODELIV,
                              MODE_PAIE: row.bonliv?.MODE_PAIE,
                              REMARQUE: row.bonliv?.REMARQUE,
                              client: row.bonliv?.client,
                              // Overview metadata for printing without fetching (includes itemCount per etage)
                              overviewEtages: row.allEtages,
                              overviewTotalItems: row.totalItems,
                              currentEtage: row.Etage,
                              currentEtageCount: row.itemCount
                            } : row;
                            await printEmplacementForBonliv(header);
                            if (activeTab === 0) {
                              try {
                                await markBonlivPerEtagePrinted({ refBL: row.REF_BL, etages: [row.Etage] });
                                refetch();
                              } catch (e) {
                                console.error('Failed to mark printed', e);
                              }
                            }
                          }}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          <PrinterOutlined />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Client:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {activeTab === 0 ? (row.bonliv?.CLIENT || 'N/A') : (row.CLIENT || 'N/A')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Articles:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {activeTab === 0 ? `${row.itemCount}/${row.totalItems}` : row.detailBonlivs?.length || 0}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Date:</Typography>
                        <Typography variant="body2">
                          {activeTab === 0 
                            ? (row.bonliv?.DATE_BL ? format(new Date(row.bonliv.DATE_BL), 'dd/MM/yyyy') : 'N/A')
                            : (row.DATE_BL ? format(new Date(row.DATE_BL), 'dd/MM/yyyy') : 'N/A')
                          }
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Mode Liv:</Typography>
                        <Typography variant="body2">
                          {activeTab === 0 ? (row.bonliv?.MODELIV || 'N/A') : (row.MODELIV || 'N/A')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Mode Paie:</Typography>
                        <Typography variant="body2">
                          {activeTab === 0 ? (row.bonliv?.MODE_PAIE || 'N/A') : (row.MODE_PAIE || 'N/A')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    {activeTab !== 0 && (
                      <Grid item xs={6} sm={2}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Statut:</Typography>
                          <Chip 
                            label={row.status || 'N/A'} 
                            color={row.status === 'VALIDE' ? 'success' : 'warning'}
                            size="small"
                            sx={{ height: 20, '& .MuiChip-label': { px: 0.8, py: 0 } }}
                          />
                          {/* Complementary order badge for non-preparation tabs */}
                          {isComplementaryOrder(row.REMARQUE) && (
                            <Chip 
                              label="COMP" 
                              color="secondary"
                              variant="outlined"
                              size="small"
                              sx={{ ml: 0.5, height: 20, '& .MuiChip-label': { px: 0.8, py: 0 } }}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  
                  {(activeTab !== 0 || row.bonliv?.REMARQUE || row.REMARQUE) && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Remarque:</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {activeTab === 0 ? (row.bonliv?.REMARQUE || 'N/A') : (row.REMARQUE || 'N/A')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {activeTab === 0 && row.allEtages && row.allEtages.filter(etageInfo => 
                    (typeof etageInfo === 'object' ? etageInfo.etage : etageInfo) !== row.Etage
                  ).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Autres Étages:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                         {row.allEtages
                            .filter(etageInfo => 
                              (typeof etageInfo === 'object' ? etageInfo.etage : etageInfo) !== row.Etage
                            )
                            .map(etageInfo => {
                              // Handle both old format (number) and new format (object with etage, status, and itemCount)
                              const etageNumber = typeof etageInfo === 'object' ? etageInfo.etage : etageInfo;
                              const etageStatus = typeof etageInfo === 'object' ? etageInfo.status : 'ATTENTE';
                              const etageItemCount = typeof etageInfo === 'object' ? etageInfo.itemCount : null;
                              
                              return (
                                <Chip 
                                  key={etageNumber} 
                                  label={etageItemCount !== null ? `Étage ${etageNumber} (${etageItemCount})` : `Étage ${etageNumber}`} 
                                  size="small" 
                                  onClick={() => handleEtageChange(etageNumber.toString())}
                                  color={etageStatus === 'SIGNALE' ? 'success' : 'error'}
                                  variant="filled"
                                  sx={{ height: 20, '& .MuiChip-label': { px: 0.8, py: 0 } }}
                                />
                              );
                            })}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6">Aucune donnée trouvée.</Typography>
            </Paper>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap' }}>
          <Box sx={{ my: 1 }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="rows-per-page-label">Par page</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Par page"
              >
                {[10, 20, 50, 100].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Pagination 
            count={pageCount} 
            page={page + 1} 
            onChange={handleChangePage} 
            color="primary" 
            sx={{ my: 1 }}
          />
        </Box>

        {/* Print Selection Modal */}
        <Dialog
          open={printModalOpen}
          onClose={() => {
            setPrintModalOpen(false);
            setPrintModalStatusFilter('all');
            setPrintModalImprimerTab('nonImprime');
            setPrintSelection(new Set());
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Imprimer - Sélectionner les bons</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Cochez les lignes à imprimer. Colonnes: Réf BL, Compte, Client, Type, Statut.
                {activeTab === 0 && activeEtage !== 'all' && (
                  <span> (Étage {activeEtage})</span>
                )}
              </Typography>
              {/* Imprimer filter Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tabs 
                  value={printModalImprimerTab} 
                  onChange={(e, newValue) => setPrintModalImprimerTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Non imprimés" value="nonImprime" />
                  <Tab label="Imprimés" value="imprime" />
                </Tabs>
              </Box>
              {/* Status Filter Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={printModalStatusFilter} 
                  onChange={(e, newValue) => setPrintModalStatusFilter(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Tous" value="all" />
                  <Tab label="SIGNALE" value="SIGNALE" />
                  <Tab label="EN ATTENTE" value="ATTENTE" />
                </Tabs>
              </Box>
            </Box>
            <Box component="div" sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ textAlign: 'left', padding: '8px' }}>
                      <Checkbox
                        size="small"
                        checked={(() => {
                          const modalData = (() => {
                            let filteredData = activeTab === 0 ? overviewData : 
                                              activeTab === 1 ? preparedBonlivs : 
                                              valideBonlivs;
                            
                            // Filter by etage for tab 0
                            if (activeTab === 0 && activeEtage !== 'all') {
                              filteredData = filteredData.filter(row => String(row.Etage) === String(activeEtage));
                            }
                            
                            // Filter by printed tab
                            if (printModalImprimerTab === 'nonImprime') {
                              filteredData = filteredData.filter(row => row.imprimer !== true);
                            } else if (printModalImprimerTab === 'imprime') {
                              filteredData = filteredData.filter(row => row.imprimer === true);
                            }
                            
                            // Filter by status
                            if (printModalStatusFilter !== 'all') {
                              filteredData = filteredData.filter(row => row.status === printModalStatusFilter);
                            }
                            
                            return filteredData;
                          })();
                          return modalData.length > 0 && modalData.every((row) => printSelection.has(row.REF_BL));
                        })()}
                        indeterminate={(() => {
                          const modalData = (() => {
                            let filteredData = activeTab === 0 ? overviewData : 
                                              activeTab === 1 ? preparedBonlivs : 
                                              valideBonlivs;
                            
                            if (activeTab === 0 && activeEtage !== 'all') {
                              filteredData = filteredData.filter(row => String(row.Etage) === String(activeEtage));
                            }
                            
                            if (printModalImprimerTab === 'nonImprime') {
                              filteredData = filteredData.filter(row => row.imprimer !== true);
                            } else if (printModalImprimerTab === 'imprime') {
                              filteredData = filteredData.filter(row => row.imprimer === true);
                            }
                            
                            if (printModalStatusFilter !== 'all') {
                              filteredData = filteredData.filter(row => row.status === printModalStatusFilter);
                            }
                            
                            return filteredData;
                          })();
                          return modalData.some((row) => printSelection.has(row.REF_BL)) && 
                                 !modalData.every((row) => printSelection.has(row.REF_BL));
                        })()}
                        onChange={(e) => {
                          const modalData = (() => {
                            let filteredData = activeTab === 0 ? overviewData : 
                                              activeTab === 1 ? preparedBonlivs : 
                                              valideBonlivs;
                            
                            if (activeTab === 0 && activeEtage !== 'all') {
                              filteredData = filteredData.filter(row => String(row.Etage) === String(activeEtage));
                            }
                            
                            if (printModalImprimerTab === 'nonImprime') {
                              filteredData = filteredData.filter(row => row.imprimer !== true);
                            } else if (printModalImprimerTab === 'imprime') {
                              filteredData = filteredData.filter(row => row.imprimer === true);
                            }
                            
                            if (printModalStatusFilter !== 'all') {
                              filteredData = filteredData.filter(row => row.status === printModalStatusFilter);
                            }
                            
                            return filteredData;
                          })();
                          const next = new Set(printSelection);
                          if (e.target.checked) {
                            modalData.forEach((row) => next.add(row.REF_BL));
                          } else {
                            modalData.forEach((row) => next.delete(row.REF_BL));
                          }
                          setPrintSelection(next);
                        }}
                      />
                    </Box>
                    <Box component="th" sx={{ textAlign: 'left', padding: '8px' }}>Réf BL</Box>
                    <Box component="th" sx={{ textAlign: 'left', padding: '8px' }}>Compte</Box>
                    <Box component="th" sx={{ textAlign: 'left', padding: '8px' }}>Client</Box>
                    <Box component="th" sx={{ textAlign: 'left', padding: '8px' }}>Type</Box>
                    <Box component="th" sx={{ textAlign: 'left', padding: '8px' }}>Statut</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {(() => {
                    // Filter data for modal: by etage (for tab 0) and by status filter
                    let modalData = activeTab === 0 ? overviewData : 
                                   activeTab === 1 ? preparedBonlivs : 
                                   valideBonlivs;
                    
                    // Filter by selected etage for tab 0
                    if (activeTab === 0 && activeEtage !== 'all') {
                      modalData = modalData.filter(row => String(row.Etage) === String(activeEtage));
                    }
                    
                    // Filter by printed tab
                    if (printModalImprimerTab === 'nonImprime') {
                      modalData = modalData.filter(row => row.imprimer !== true);
                    } else if (printModalImprimerTab === 'imprime') {
                      modalData = modalData.filter(row => row.imprimer === true);
                    }
                    
                    // Filter by status
                    if (printModalStatusFilter !== 'all') {
                      modalData = modalData.filter(row => row.status === printModalStatusFilter);
                    }
                    
                    // Helper function to detect type from multiple sources
                    const detectType = (row) => {
                      let ajd = false;
                      let nossoins = false;
                      let surplace = false;
                      
                      // Get REMARQUE and MODELIV for checking
                      let remarque = '';
                      let modeliv = '';
                      
                      if (activeTab === 0) {
                        // For preparation tab, check bonliv object
                        ajd = !!row.bonliv?.AJD;
                        nossoins = !!row.bonliv?.NOS_SOINS;
                        surplace = !!row.bonliv?.SUR_PLACE;
                        remarque = (row.bonliv?.REMARQUE || '').toUpperCase();
                        modeliv = (row.bonliv?.MODELIV || '').toUpperCase();
                      } else {
                        // For other tabs, check row directly
                        ajd = !!row.AJD;
                        nossoins = !!row.NOS_SOINS;
                        surplace = !!row.SUR_PLACE;
                        remarque = (row.REMARQUE || '').toUpperCase();
                        modeliv = (row.MODELIV || '').toUpperCase();
                      }
                      
                      // Also check REMARQUE and MODELIV as they may contain the type info
                      // even if flags are not set correctly
                      if (!ajd) {
                        ajd = remarque.includes('AJD') || modeliv.includes('AJD');
                      }
                      if (!nossoins) {
                        nossoins = remarque.includes('NOS SOINS') || remarque.includes('NOS_NOINS') ||
                                  modeliv.includes('NOS SOINS') || modeliv.includes('NOS_NOINS');
                      }
                      if (!surplace) {
                        surplace = remarque.includes('SUR PLACE') || remarque.includes('SURPLACE') ||
                                  modeliv.includes('SUR PLACE') || modeliv.includes('SURPLACE');
                      }
                      
                      // Priority: AJD > Nos Soins > Sur Place > Normal
                      if (ajd) return 'AJD';
                      if (nossoins) return 'Nos Soins';
                      if (surplace) return 'Sur Place';
                      return 'Normal';
                    };
                    
                    return modalData.map((row) => {
                      const typeLabel = detectType(row);
                      // Get COMPTE from the correct location for tab 0
                      const compte = activeTab === 0 
                        ? (row.bonliv?.COMPTE || 
                           row.COMPTE || 
                           (row.bonliv?.client && (row.bonliv.client.COMPTE || row.bonliv.client.compte)) ||
                           '')
                        : (row.COMPTE || '');
                      const client = activeTab === 0 ? (row.bonliv?.CLIENT || '') : (row.CLIENT || '');
                      const status = row.status || 'ATTENTE';
                      const statusLabel = status === 'SIGNALE' ? 'SIGNALE' : status === 'ATTENTE' ? 'EN ATTENTE' : status;
                      return (
                        <Box component="tr" key={row.REF_BL} sx={{ borderTop: '1px solid #eee' }}>
                          <Box component="td" sx={{ padding: '8px' }}>
                            <Checkbox
                              size="small"
                              checked={printSelection.has(row.REF_BL)}
                              onChange={(e) => {
                                const next = new Set(printSelection);
                                if (e.target.checked) next.add(row.REF_BL); else next.delete(row.REF_BL);
                                setPrintSelection(next);
                              }}
                            />
                          </Box>
                          <Box component="td" sx={{ padding: '8px' }}>{row.REF_BL}</Box>
                          <Box component="td" sx={{ padding: '8px' }}>{compte}</Box>
                          <Box component="td" sx={{ padding: '8px' }}>{client}</Box>
                          <Box component="td" sx={{ padding: '8px' }}>{typeLabel}</Box>
                          <Box component="td" sx={{ padding: '8px' }}>
                            <Chip 
                              label={statusLabel} 
                              size="small"
                              color={status === 'SIGNALE' ? 'success' : 'default'}
                              variant={status === 'SIGNALE' ? 'filled' : 'outlined'}
                            />
                          </Box>
                        </Box>
                      );
                    });
                  })()}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <PermissionButton   permissionLibrary="mui" onClick={() => setPrintModalOpen(false)} color="inherit">
              Annuler
            </PermissionButton>
            <Button
              onClick={async () => {
                // Filter data same way as modal display
                let modalData = activeTab === 0 ? overviewData : 
                               activeTab === 1 ? preparedBonlivs : 
                               valideBonlivs;
                
                if (activeTab === 0 && activeEtage !== 'all') {
                  modalData = modalData.filter(row => String(row.Etage) === String(activeEtage));
                }
                
                // Filter by status
                if (printModalStatusFilter !== 'all') {
                  modalData = modalData.filter(row => row.status === printModalStatusFilter);
                }
                
                const selectedHeaders = modalData
                  .filter((row) => printSelection.has(row.REF_BL))
                  .map((row) => {
                    if (activeTab === 0) {
                      // Get COMPTE from the correct location
                      const compte = row.bonliv?.COMPTE || 
                                     row.COMPTE || 
                                     (row.bonliv?.client && (row.bonliv.client.COMPTE || row.bonliv.client.compte)) ||
                                     'N/A';
                      
                      return {
                        id: row.bonliv?.id || row.id, // Pass ID so print utility can fetch COMPTE
                        REF_BL: row.REF_BL,
                        REF_DEV: row.bonliv?.REF_DEV || row.REF_DEV,
                        DATE_BL: row.bonliv?.DATE_BL || row.DATE_BL,
                        COMPTE: compte, // Pass COMPTE directly
                        CLIENT: row.bonliv?.CLIENT || row.CLIENT,
                        VILLE: row.bonliv?.client?.VILLE || row.bonliv?.VILLE || row.VILLE,
                        ADRESSE: row.bonliv?.client?.ADRESSE_SIEGE || row.bonliv?.ADRESSE || row.ADRESSE,
                        MODELIV: row.bonliv?.MODELIV || row.MODELIV,
                        MODE_PAIE: row.bonliv?.MODE_PAIE || row.MODE_PAIE,
                        REMARQUE: row.bonliv?.REMARQUE || row.REMARQUE,
                        client: row.bonliv?.client || row.client,
                        // Pass allEtages which includes itemCount per etage
                        overviewEtages: row.allEtages,
                        overviewTotalItems: row.totalItems,
                        currentEtage: row.Etage,
                        currentEtageCount: row.itemCount
                      };
                    }
                    return row;
                  });
                if (selectedHeaders.length > 0) {
                  const { printEmplacementForBonlivs } = await import('./PreparationEmplacementPrintUtility');
                  await printEmplacementForBonlivs(selectedHeaders);
                }

                // Mark printed in backend for selected rows (Preparation tab only)
                if (activeTab === 0) {
                  try {
                    // Build map of REF_BL -> Set(etages) for selected rows
                    const selectedMap = new Map();
                    modalData
                      .filter((row) => printSelection.has(row.REF_BL))
                      .forEach((row) => {
                        const et = row.Etage;
                        if (!selectedMap.has(row.REF_BL)) selectedMap.set(row.REF_BL, new Set());
                        selectedMap.get(row.REF_BL).add(et);
                      });

                    // Call API per REF_BL
                    for (const [refBL, etagesSet] of selectedMap.entries()) {
                      const etages = Array.from(etagesSet);
                      if (etages.length > 0) {
                        // eslint-disable-next-line no-await-in-loop
                        await markBonlivPerEtagePrinted({ refBL, etages });
                      }
                    }

                    refetch();
                  } catch (e) {
                    console.error('Failed to mark printed selection', e);
                  }
                }
                setPrintModalOpen(false);
              }}
              variant="contained"
              color="primary"
              disabled={printSelection.size === 0}
            >
              Imprimer ({printSelection.size})
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const getTitle = () => {
    return activeTab === 0 ? 'Aperçu de Préparation' : 
           activeTab === 1 ? 'Aperçu de Contrôle' : 
           activeTab === 2 ? 'Aperçu des Validés' : 
           'Aperçu de Collecte';
  };

  return (
    <Container maxWidth="xl" style={{ marginTop: '2rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" gutterBottom>
            {getTitle()}
          </Typography>
          <Tooltip title="Rafraîchir les données">
            <IconButton
              color="primary"
              onClick={() => {
                console.log('Refresh button clicked', { activeTab, collectionRefresh: typeof collectionRefreshRef.current });
                if (activeTab === 0) refetch();
                else if (activeTab === 1) refetchPrepared();
                else if (activeTab === 2) refetchValide();
                else if (activeTab === 3 && collectionRefreshRef.current && typeof collectionRefreshRef.current === 'function') {
                  console.log('Calling collection refresh');
                  collectionRefreshRef.current();
                } else if (activeTab === 3) {
                  console.log('Collection refresh not available', { collectionRefresh: collectionRefreshRef.current });
                }
              }}
              size="small"
            >
              <ReloadOutlined />
            </IconButton>
          </Tooltip>
          {activeTab === 0 && (
            <Tooltip title="Recréer les enregistrements BonlivPerEtage pour tous les BonLivs en préparation">
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<ToolOutlined />}
                onClick={handleOpenFixDialog}
                disabled={recreateLoading}
                sx={{ textTransform: 'none' }}
              >
                {recreateLoading ? 'Correction...' : 'Corriger Étages'}
              </Button>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {visibleTabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'contained' : 'outlined'}
              onClick={() => handleTabChange(null, tab.id)}
              sx={{ 
                minWidth: { xs: '100%', sm: 120 },
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                mb: { xs: 1, sm: 0 }
              }}
              size="small"
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>
      
      <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%' }}>
        {activeTab === 0 && (
          <>
            <Box sx={{ 
              borderRight: { md: 1 }, 
              borderBottom: { xs: 1, md: 0 },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'column' },
              minWidth: { md: 200 },
              p: 1
            }}>
              {/* Entrepot Selector */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="entrepot-select-label">Entrepôt</InputLabel>
                <Select
                  labelId="entrepot-select-label"
                  value={activeEntrepot}
                  onChange={(e) => handleEntrepotChange(e.target.value)}
                  label="Entrepôt"
                >
                  <MenuItem value="all">Tous les entrepôts</MenuItem>
                  {entrepots?.map((entrepot) => (
                    <MenuItem key={entrepot.code_ent} value={entrepot.code_ent}>
                      {entrepot.code_ent} - {entrepot.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Etage Selector */}
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={activeEtage}
                onChange={(e, newValue) => handleEtageChange(newValue)}
                sx={{ 
                  display: { xs: 'none', md: 'flex' }
                }}
              >
                {tabItems.map((item, index) => (
                  <Tab 
                    key={item.key} 
                    label={item.label} 
                    value={item.key}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
              
              <Tabs
                orientation="horizontal"
                variant="scrollable"
                value={activeEtage}
                onChange={(e, newValue) => handleEtageChange(newValue)}
                sx={{ 
                  display: { xs: 'flex', md: 'none' },
                  mb: 1
                }}
              >
                {tabItems.map((item, index) => (
                  <Tab 
                    key={item.key} 
                    label={item.label} 
                    value={item.key}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
            </Box>
            
            <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 } }}>
              {renderCardView()}
            </Box>
          </>
        )}
        
        {(activeTab === 1 || activeTab === 2) && (
          <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 } }}>
            {renderCardView()}
          </Box>
        )}

        {activeTab === 3 && (
          <CollectionOverviewList 
            onViewDetails={onViewDetails} 
            onRefresh={(refreshFn) => {
              console.log('Setting collection refresh function', typeof refreshFn);
              collectionRefreshRef.current = refreshFn;
            }} 
          />
        )}
      </Box>
      
      {/* Fix BonlivPerEtage Confirmation Dialog */}
      <Dialog
        open={fixDialogOpen}
        onClose={handleCloseFixDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Corriger les Enregistrements BonlivPerEtage
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action va recréer tous les enregistrements <strong>BonlivPerEtage</strong> pour les BonLivs en statut <strong>EN_PREPARATION</strong>.
            <br /><br />
            <strong>Ce qui sera fait :</strong>
            <ul>
              <li>✓ Suppression des anciens enregistrements BonlivPerEtage</li>
              <li>✓ Lecture des données Stock actuelles (avec Étage/Zone)</li>
              <li>✓ Regroupement des articles par Étage</li>
              <li>✓ Création de nouveaux enregistrements BonlivPerEtage corrects</li>
            </ul>
            <br />
            Les articles sans stock ou avec Étage NULL seront assignés à l'Étage 0.
            <br /><br />
            <strong>Voulez-vous continuer ?</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFixDialog} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handleExecuteFix} 
            color="secondary" 
            variant="contained"
            disabled={recreateLoading}
            startIcon={recreateLoading ? <CircularProgress size={16} /> : <ToolOutlined />}
          >
            {recreateLoading ? 'Correction en cours...' : 'Corriger'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PreparationOverviewList;